
const CHAT_URL = `${process.env.REACT_APP_CHAT_API_BASE_URL}/api/chat`;
const EARLY_ACCESS_URL = `${process.env.REACT_APP_CHAT_API_BASE_URL}/api/early-access`;
const SUGGESTED_QUESTIONS_URL = `${process.env.REACT_APP_CHAT_API_BASE_URL}/api/suggested-questions`;


let conversationId: string = crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function resetSession(): void {
  conversationId = crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getConversationId(): string {
  return conversationId;
}

function getChatId(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/* ── Early Access Form ──────────────────────────────────────────────────────── */

export interface EarlyAccessPayload {
  fullName: string;
  email: string;
  agencyName: string;
  isCurrentCustomer: boolean;
}

export interface EarlyAccessResponse {
  message: string;
  duplicate: boolean;
}

export async function submitEarlyAccess(payload: EarlyAccessPayload): Promise<EarlyAccessResponse> {
  const response = await fetch(EARLY_ACCESS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      full_name: payload.fullName,
      email: payload.email,
      agency_name: payload.agencyName || null,
      is_customer: payload.isCurrentCustomer ? 'yes' : 'no',
    }),
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

/* ── Request Access ─────────────────────────────────────────────────────────── */

const REQUEST_ACCESS_URL = `${process.env.REACT_APP_Auth_Url}/api/account/requestaccess`;

export interface RequestAccessPayload {
  AgencyName: string;
  AgencyId: string;
  RequestedAgentId: string;
}

export async function requestAccess(payload: RequestAccessPayload): Promise<void> {
  const response = await fetch(REQUEST_ACCESS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
}

/* ── Suggested Questions ───────────────────────────────────────────────────── */

export async function fetchSuggestedQuestions(query: string): Promise<string[]> {
  try {
    const response = await fetch(`${SUGGESTED_QUESTIONS_URL}?q=${encodeURIComponent(query)}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.suggestions || [];
  } catch {
    return [];
  }
}

/* ── Marketing Chat (simple text stream) ────────────────────────────────────── */

export async function sendMarketingMessageStream(
  question: string,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: Error) => void
): Promise<void> {
  try {
    const response = await fetch(CHAT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        conversation_id: getConversationId(),
        chat_id: getChatId(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr) continue;
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.type === 'answer') {
            if (parsed.Answer) onChunk(parsed.Answer);
            if (parsed.ResponseLoading === false) {
              reader.cancel();
              break;
            }
          }
        } catch {
          // skip malformed line
        }
      }
    }

    onDone();
  } catch (err) {
    onError(err as Error);
  }
}
